import React from 'react';
import { useAppStore } from '../../store/appStore';
import Avatar from '../common/Avatar';

// hideFor: roles that should NOT see the item. adminOnly: only the admin role.
const NAV = [
  { id: 'cameras', label: 'Cameras', icon: CamIcon },
  { id: 'notifications', label: 'Notifications', icon: BellIcon },
  { id: 'schedule', label: 'Schedule', icon: CalIcon },
  { id: 'events', label: 'Events', icon: EventIcon, hideFor: ['customer_operator', 'volunteer_operator'] },
  { id: 'audit', label: 'Audit Log', icon: AuditIcon, hideFor: ['customer_operator', 'volunteer_operator'] },
  { id: 'users', label: 'Users', icon: UsersIcon, adminOnly: true },
  { id: 'companies', label: 'Companies', icon: CompanyIcon, adminOnly: true },
  { id: 'system', label: 'System', icon: SystemIcon, adminOnly: true },
];

function navVisible(item, role) {
  if (item.adminOnly) return role === 'admin';
  if (item.hideFor) return !item.hideFor.includes(role);
  return true;
}

function BellIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>;
}
function UsersIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>;
}
function CompanyIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/><path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01"/></svg>;
}
function SystemIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>;
}

function CalIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
}

function CamIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M20.3 6.3L17 9.1A5 5 0 0112 7a5 5 0 00-5 5 5 5 0 005 5 5 5 0 004.4-2.6l3.7 2.4A9 9 0 0112 21 9 9 0 013 12 9 9 0 0112 3a9 9 0 018 4.6"/></svg>;
}
function EventIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>;
}
function AuditIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>;
}
export default function Sidebar() {
  const { activePanel, setActivePanel, cameras, logout, user, unreadNotifications } = useAppStore();
  const online = cameras.filter(c => c.status?.online).length;
  const offline = cameras.filter(c => c.status && !c.status.online).length;

  return (
    <div style={{
      width: '220px',
      flexShrink: 0,
      background: 'var(--bg-surface)',
      borderRight: '1px solid var(--border-dim)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Logo */}
      <div style={{
        padding: '14px 16px',
        borderBottom: '1px solid var(--border-dim)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <img
          src="/hdontap_logo.svg"
          alt="HDOnTap"
          style={{ height: '44px', width: 'auto', maxWidth: '188px' }}
        />
      </div>

      {/* Status summary */}
      <div style={{
        margin: '12px 12px 4px',
        padding: '10px 12px',
        background: 'var(--bg-primary)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-dim)',
      }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '10px', fontWeight: 600, letterSpacing: '0.12em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '8px' }}>
          System Status
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 700, color: 'var(--status-online)', lineHeight: 1 }}>{online}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>ONLINE</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 700, color: offline > 0 ? 'var(--accent-red)' : 'var(--text-muted)', lineHeight: 1 }}>{offline}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>OFFLINE</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 700, color: 'var(--text-secondary)', lineHeight: 1 }}>{cameras.length}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>TOTAL</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '8px 8px', overflow: 'auto' }}>
        {NAV.filter(item => navVisible(item, user?.role)).map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActivePanel(id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              width: '100%', padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: activePanel === id ? 'rgba(244,123,32,0.12)' : 'transparent',
              color: activePanel === id ? 'var(--accent-orange)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontFamily: 'var(--font-display)',
              fontSize: '13px',
              fontWeight: activePanel === id ? 700 : 500,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              borderLeft: activePanel === id ? '2px solid var(--accent-orange)' : '2px solid transparent',
              transition: 'all 0.15s',
              marginBottom: '2px',
            }}
          >
            <Icon />
            {label}
            {id === 'notifications' && unreadNotifications > 0 && (
              <span style={{
                marginLeft: 'auto',
                minWidth: '18px', height: '18px', padding: '0 5px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--accent-orange)', color: 'var(--bg-void)',
                borderRadius: '999px', fontSize: '10px', fontWeight: 800,
                fontFamily: 'var(--font-display)',
              }}>
                {unreadNotifications > 99 ? '99+' : unreadNotifications}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* User footer */}
      <div style={{
        padding: '12px',
        borderTop: '1px solid var(--border-dim)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}>
        <button
          onClick={() => setActivePanel('account')}
          title="Edit your profile"
          style={{
            flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '10px',
            background: activePanel === 'account' ? 'rgba(244,123,32,0.12)' : 'transparent',
            border: 'none', borderRadius: 'var(--radius-md)', padding: '4px 6px',
            cursor: 'pointer', textAlign: 'left',
          }}
        >
          <Avatar src={user?.avatar} name={user?.username} userId={user?.id} size={32} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: activePanel === 'account' ? 'var(--accent-orange)' : 'var(--text-primary)', fontFamily: 'var(--font-display)', letterSpacing: '0.05em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.username}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
              {user?.role}
            </div>
          </div>
        </button>
        <button onClick={logout} title="Logout" style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          padding: '4px',
          borderRadius: 'var(--radius-sm)',
          display: 'flex',
          transition: 'color 0.15s',
        }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--accent-red)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/>
          </svg>
        </button>
      </div>
    </div>
  );
}
